/**
 * Class representing a modal popup
 *
 * constructor should have html passed to
 * in order to get the content to be displayed
 *
 * const modal = modalHtmlInit({html});
 $(modal.domElems.currentElem).on('close', function () {
    // make something
 })
 *
 */

class Modal {
  /**
   * Create a modal popup
   * @param {string} props.type - The type of current modal. (default "modal")
   * @param {string} props.html - The html content which be appended in simple modal container.
   * @returns {object}
   */

  constructor({
    type = 'modal',
    wrapperClass = '',
    isBodyBlured = true,
    html = null,
    callback = null,
    dataClass = '',
    renderHeader
  }) {
    // initializing properties
    this.domElems = {};
    this.type = type;
    this.id = this.setRandomId();
    this.wrapperClass = wrapperClass;
    this.dataClass = dataClass;
    this.html = html;
    this.callback = callback;
    this.renderHeader = renderHeader;
    this.isBodyBlured = isBodyBlured;
    this.isBodyFixed = document.body.style.position === 'fixed';
  }

  show() {
    const init = () => {
      this.addBodyBlured();
      this.disableScroll();
      this.appendBody();
      this.callback?.(this.close.bind(this));
      this.setCurrentDomElems();
      this.setHeightWidth();
      this.setCloseClickHandlers();
      this.setCurrentShown();
    };

    // check render html
    if (this.html) {
      this.data = this.html;
      init();
      return this;
    }
  }

  /**
   * choose if popup of current type is enabled add class to show fast popup
   * else add class to show slow
   * @returns {boolean}
   */

  setCurrentShown() {
    if (Modal.CurrentShown[this.type]) {
      Modal.CurrentShown[this.type].domElems.currentElem.remove();
      this.domElems.currentElem.classList.add(`${this.type}--show-fast`);
    } else {
      window.setTimeout(() => {
        this.domElems.currentElem.classList.add(`${this.type}--show-slow`);
      }, 50);
    }

    Modal.CurrentShown[this.type] = this;
  }

  appendBody() {
    document.body.insertAdjacentHTML('beforeend', this.renderTemplate());
  }

  addBodyBlured() {
    if (this.isBodyBlured) {
      document.body.classList.add('body-blured');
    }
  }

  initSetHeightWidth = () => {
    if (this.isBodyBlured
      && this.domElems.currentDataContainer.offsetHeight
      && this.domElems.currentDataContainer.offsetWidth
    ) {
      this.domElems.currentContainer.style.height = `${this.domElems.currentDataContainer.offsetHeight}px`;
      this.domElems.currentContainer.style.width = `${this.domElems.currentDataContainer.offsetWidth}px`;
    }
  };

  /**
   * make the size of main container equal to the size of its innerHtml
   * @returns {boolean}
   */

  setHeightWidth() {
    this.initSetHeightWidth();
    window.addEventListener('resize', this.initSetHeightWidth);
  }

  /**
   * set Dom elements of current elem to domElem property
   */

  setCurrentDomElems() {
    this.domElems.currentElem = document.getElementById(this.id);
    this.domElems.currentClose = this.domElems.currentElem.querySelector('.modal__close');
    this.domElems.currentContainer = this.domElems.currentElem.querySelector('.modal__main-container');
    this.domElems.currentDataContainer = this.domElems.currentElem.querySelector('.modal__data-container');
  }

  /**
   * returns unique id for current modal
   * @returns {string}
   */

  setRandomId() {
    let id;

    do {
      id = `${this.type}_${Math.random().toString(36).slice(2)}`;
    } while (Modal.CurrentShown[this.type] && Modal.CurrentShown[this.type].id === id);
    return id;
  }

  /**
   * returns main template for modal popups
   * @returns {string}
   */

  renderTemplate() {
    return `
    <div class="modal ${this.wrapperClass}" id ="${this.id}">
      <div class="modal__main-container">
        ${this.renderHeader ? this.renderHeader() : '<div class="modal__close"></div>'}
        <div class="modal__data-container ${this.dataClass}">${this.data}</div>
      </div>
    </div>`;
  }

  handleOuterClick = e => {
    if (e.target.classList.contains(this.type)) {
      this.domElems.currentClose?.dispatchEvent(new CustomEvent('click', {detail: {clickedOutside: true}}));
    }
  };

  escapeButtonHandler = e => {
    if (e.keyCode === 27) {
      this.close('escape');
    }
  };

  enableScroll = () => {
    if (this.isBodyFixed) {
      return;
    }

    const scrollY = document.body.style.top;

    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
  }

  disableScroll = () => {
    if (this.isBodyFixed) {
      return;
    }

    document.body.style.top = `-${window.scrollY}px`;
    document.body.style.position = 'fixed';
  }

  /**
   * set close handlers which trigger custom events on this Dom elem
   */

  setCloseClickHandlers() {
    this.domElems.currentClose?.addEventListener(
      'click', e => this.close(e.detail.clickedOutside ? 'outsideModal' : 'closeBtn'), {once: true}
    );

    const $additionalCloseButton = this.domElems.currentElem.querySelector('.js-close-modal-btn');

    $additionalCloseButton && $additionalCloseButton.addEventListener(
      'click', () => this.close('closeBtn'), {once: true}
    );

    this.domElems.currentElem.addEventListener('click', this.handleOuterClick);
    window.addEventListener('keyup', this.escapeButtonHandler);
  }

  /**
   * close modal popup after change opacity transition
   * @param clickedOn {'closeBtn' | 'outsideModal' | 'escape'}
   */

  close(clickedOn) {
    document.body.classList.remove('body-blured');
    this.domElems.currentElem.classList.remove(`${this.type}--show-slow`);
    this.domElems.currentElem.classList.remove(`${this.type}--show-fast`);
    window.setTimeout(() => {
      this.domElems.currentElem.remove();
      this.enableScroll();
    }, 400);

    window.removeEventListener('resize', this.initSetHeightWidth);
    window.removeEventListener('keyup', this.escapeButtonHandler);
    this.domElems.currentElem.removeEventListener('click', this.handleOuterClick);

    this.domElems.currentElem.dispatchEvent(new CustomEvent('close', {bubbles: true, detail: {clickedOn}}));
    delete Modal.CurrentShown[this.type];
  }

  /**
   *
   * @param props
   * @returns {Modal}
   */

  static initHtml(props) {
    return new Modal(props);
  }
}

// static property which includes currently opened modal popups

Modal.CurrentShown = {};

export const modalHtmlInit = Modal.initHtml;
